import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { usersApi, organizationsApi } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  KeyIcon,
  XMarkIcon,
  CheckBadgeIcon,
  NoSymbolIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Roles are kept here as a single source for the dropdown + labels.
// The server-side enum lives in backend/src/models/schema.ts and must
// stay in sync — if you add a role, add it in both places.
const ROLES = ['super_admin', 'ministry_admin', 'hospital_admin', 'dispatcher', 'paramedic', 'viewer'] as const;
type UserRole = (typeof ROLES)[number];

const roleLabels: Record<UserRole, string> = {
  super_admin:     'مسؤول عام',
  ministry_admin:  'مسؤول وزارة',
  hospital_admin:  'مسؤول مستشفى',
  dispatcher:      'موزّع',
  paramedic:       'مسعف',
  viewer:          'مشاهد',
};

const roleBadgeClass: Record<UserRole, string> = {
  super_admin:    'bg-purple-100 text-purple-700',
  ministry_admin: 'bg-blue-100 text-blue-700',
  hospital_admin: 'bg-teal-100 text-teal-700',
  dispatcher:     'bg-orange-100 text-orange-700',
  paramedic:      'bg-emerald-100 text-emerald-700',
  viewer:         'bg-gray-100 text-gray-700',
};

const createUserSchema = z.object({
  email: z.string().email('بريد إلكتروني غير صالح'),
  password: z.string().min(8, 'كلمة المرور يجب أن تكون 8 أحرف على الأقل'),
  fullName: z.string().min(2, 'الاسم الكامل مطلوب'),
  phone: z.string().optional(),
  role: z.enum(ROLES),
  organizationId: z.string().uuid().optional().or(z.literal('')),
  mustChangePassword: z.boolean().optional(),
});
type CreateUserForm = z.infer<typeof createUserSchema>;

const editUserSchema = z.object({
  email: z.string().email('بريد إلكتروني غير صالح'),
  fullName: z.string().min(2, 'الاسم الكامل مطلوب'),
  phone: z.string().optional(),
  role: z.enum(ROLES),
  organizationId: z.string().uuid().optional().or(z.literal('')),
});
type EditUserForm = z.infer<typeof editUserSchema>;

const resetPwSchema = z.object({
  newPassword: z.string().min(8, 'كلمة المرور يجب أن تكون 8 أحرف على الأقل'),
  mustChangePassword: z.boolean().optional(),
});
type ResetPwForm = z.infer<typeof resetPwSchema>;

interface UserRow {
  id: string;
  email: string;
  fullName: string;
  phone?: string | null;
  role: UserRole;
  organizationId?: string | null;
  organization?: { id: string; nameAr?: string; name?: string | null } | null;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLogin?: string | null;
  createdAt: string;
}

export default function Users() {
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const queryClient = useQueryClient();

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');  // '', 'true', 'false'

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [resettingPw, setResettingPw] = useState<UserRow | null>(null);
  const [deleting, setDeleting] = useState<UserRow | null>(null);

  /**
   * The search term is debounced before it reaches the query key.
   *
   * A query key change always misses the cache, so binding the raw input value
   * straight into the key issued one `GET /users` per keystroke — typing a
   * full name was ~20 requests in three seconds, and a fast typist could
   * outrun the server's rate limit on their own. Only the settled value is
   * used for fetching; the input itself stays fully responsive.
   */
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  // Queries
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', debouncedSearch, roleFilter, statusFilter],
    queryFn: async () => {
      const params: any = {};
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.isActive = statusFilter;
      const resp = await usersApi.getAll(params);
      return resp.data.data as UserRow[];
    },
  });

  const { data: orgs } = useQuery({
    queryKey: ['organizations-min'],
    queryFn: async () => {
      const resp = await organizationsApi.getAll();
      return resp.data.data as Array<{ id: string; name?: string | null; nameAr?: string }>;
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateUserForm) =>
      usersApi.create({
        ...data,
        organizationId: data.organizationId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('تم إنشاء المستخدم بنجاح');
      setCreateOpen(false);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'فشل في إنشاء المستخدم'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditUserForm }) =>
      usersApi.update(id, {
        ...data,
        organizationId: data.organizationId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('تم تحديث المستخدم بنجاح');
      setEditing(null);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'فشل في تحديث المستخدم'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => usersApi.toggleActive(id),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(resp.data.message || 'تم تحديث الحالة');
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'فشل في تغيير الحالة'),
  });

  const resetPwMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ResetPwForm }) =>
      usersApi.resetPassword(id, data.newPassword, data.mustChangePassword ?? true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('تمت إعادة تعيين كلمة المرور');
      setResettingPw(null);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'فشل في إعادة التعيين'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('تم حذف المستخدم');
      setDeleting(null);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'فشل في الحذف'),
  });

  const users = usersData || [];

  return (
    <div dir="rtl" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">المستخدمون</h1>
          <p className="text-sm text-gray-500 mt-1">إدارة حسابات المستخدمين والصلاحيات</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px]"
        >
          <PlusIcon className="w-5 h-5" />
          إضافة مستخدم
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="بحث بالاسم أو البريد أو الهاتف..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">كل الأدوار</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>{roleLabels[r]}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">كل الحالات</option>
          <option value="true">مفعّل</option>
          <option value="false">معطّل</option>
        </select>
      </div>

      {/* List
          Below md → vertical card list (the table doesn't fit on a
          phone and horizontal scroll is a usability dead-end here too).
          From md upwards → the regular table. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">جارٍ التحميل…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">لا يوجد مستخدمون مطابقون للبحث</div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {users.map((u) => (
                <div key={u.id} className="p-3 space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <UserCircleIcon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-gray-900 truncate">{u.fullName}</div>
                        {u.isActive ? (
                          <span className="inline-flex items-center gap-0.5 text-green-700 text-[11px] flex-shrink-0">
                            <CheckBadgeIcon className="w-3.5 h-3.5" /> مفعّل
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-red-700 text-[11px] flex-shrink-0">
                            <NoSymbolIcon className="w-3.5 h-3.5" /> معطّل
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate" dir="ltr">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={clsx('inline-block px-2 py-0.5 rounded-full text-[11px] font-medium', roleBadgeClass[u.role])}>
                          {roleLabels[u.role]}
                        </span>
                        {(u.organization?.nameAr || u.organization?.name) && (
                          <span className="text-[11px] text-gray-500 truncate">
                            {u.organization?.nameAr || u.organization?.name}
                          </span>
                        )}
                      </div>
                      {u.phone && <p className="text-xs text-gray-500 mt-0.5" dir="ltr">{u.phone}</p>}
                      {u.mustChangePassword && (
                        <p className="text-[11px] text-amber-600 mt-1">⚠ مطلوب تغيير كلمة المرور</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 pt-1 border-t border-gray-100">
                    <button
                      onClick={() => setEditing(u)}
                      className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs min-h-[40px]"
                    >
                      <PencilSquareIcon className="w-4 h-4" /> تعديل
                    </button>
                    <button
                      onClick={() => setResettingPw(u)}
                      className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-amber-50 text-amber-700 text-xs min-h-[40px]"
                    >
                      <KeyIcon className="w-4 h-4" /> كلمة المرور
                    </button>
                    <button
                      onClick={() => toggleMutation.mutate(u.id)}
                      disabled={toggleMutation.isPending}
                      className={clsx(
                        'flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs min-h-[40px]',
                        u.isActive ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700',
                      )}
                    >
                      {u.isActive ? <NoSymbolIcon className="w-4 h-4" /> : <CheckBadgeIcon className="w-4 h-4" />}
                      {u.isActive ? 'تعطيل' : 'تفعيل'}
                    </button>
                    {isSuperAdmin && (
                      <button
                        onClick={() => setDeleting(u)}
                        className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-red-50 text-red-700 text-xs min-h-[40px]"
                      >
                        <TrashIcon className="w-4 h-4" /> حذف
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-right">الاسم</th>
                    <th className="px-4 py-3 text-right">البريد الإلكتروني</th>
                    <th className="px-4 py-3 text-right hidden lg:table-cell">الهاتف</th>
                    <th className="px-4 py-3 text-right">الدور</th>
                    <th className="px-4 py-3 text-right hidden lg:table-cell">الجهة</th>
                    <th className="px-4 py-3 text-right">الحالة</th>
                    <th className="px-4 py-3 text-right">إجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <UserCircleIcon className="w-5 h-5 text-primary-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{u.fullName}</div>
                            {u.mustChangePassword && (
                              <div className="text-[10px] text-amber-600 mt-0.5">مطلوب تغيير كلمة المرور</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3 text-gray-700 hidden lg:table-cell">{u.phone || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={clsx('inline-block px-2 py-1 rounded-full text-xs font-medium', roleBadgeClass[u.role])}>
                          {roleLabels[u.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 hidden lg:table-cell">
                        {u.organization?.nameAr || u.organization?.name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {u.isActive ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                            <CheckBadgeIcon className="w-4 h-4" /> مفعّل
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 text-xs">
                            <NoSymbolIcon className="w-4 h-4" /> معطّل
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditing(u)}
                            className="p-1.5 rounded hover:bg-blue-50 text-blue-600 min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title="تعديل"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setResettingPw(u)}
                            className="p-1.5 rounded hover:bg-amber-50 text-amber-600 min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title="إعادة تعيين كلمة المرور"
                          >
                            <KeyIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleMutation.mutate(u.id)}
                            className={clsx(
                              'p-1.5 rounded min-h-[36px] min-w-[36px] flex items-center justify-center',
                              u.isActive ? 'hover:bg-red-50 text-red-600' : 'hover:bg-green-50 text-green-600'
                            )}
                            title={u.isActive ? 'تعطيل' : 'تفعيل'}
                            disabled={toggleMutation.isPending}
                          >
                            {u.isActive ? <NoSymbolIcon className="w-4 h-4" /> : <CheckBadgeIcon className="w-4 h-4" />}
                          </button>
                          {isSuperAdmin && (
                            <button
                              onClick={() => setDeleting(u)}
                              className="p-1.5 rounded hover:bg-red-50 text-red-600 min-h-[36px] min-w-[36px] flex items-center justify-center"
                              title="حذف"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Create modal */}
      {createOpen && (
        <CreateUserModal
          orgs={orgs || []}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isPending={createMutation.isPending}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <EditUserModal
          user={editing}
          orgs={orgs || []}
          onClose={() => setEditing(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editing.id, data })}
          isPending={updateMutation.isPending}
        />
      )}

      {/* Reset password modal */}
      {resettingPw && (
        <ResetPasswordModal
          user={resettingPw}
          onClose={() => setResettingPw(null)}
          onSubmit={(data) => resetPwMutation.mutate({ id: resettingPw.id, data })}
          isPending={resetPwMutation.isPending}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" dir="rtl">
            <Dialog.Title className="text-lg font-semibold mb-3">تأكيد الحذف</Dialog.Title>
            <p className="text-sm text-gray-600 mb-6">
              هل أنت متأكد من حذف المستخدم <strong>{deleting?.fullName}</strong>؟ لا يمكن التراجع عن هذا الإجراء.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[40px]"
              >
                إلغاء
              </button>
              <button
                onClick={() => deleting && deleteMutation.mutate(deleting.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 min-h-[40px]"
              >
                {deleteMutation.isPending ? 'جارٍ الحذف…' : 'حذف'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Modals ─────────────────────────────────────────────────────────────

function CreateUserModal({
  orgs, onClose, onSubmit, isPending,
}: {
  orgs: Array<{ id: string; name?: string | null; nameAr?: string }>;
  onClose: () => void;
  onSubmit: (data: CreateUserForm) => void;
  isPending: boolean;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<CreateUserForm>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { role: 'viewer', mustChangePassword: true },
  });

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" dir="rtl">
          <div className="flex items-center justify-between p-4 border-b">
            <Dialog.Title className="text-lg font-semibold">إضافة مستخدم جديد</Dialog.Title>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
            <Field label="الاسم الكامل" icon={<UserCircleIcon className="w-4 h-4" />} error={errors.fullName?.message}>
              <input {...register('fullName')} className="input" placeholder="مثال: علي محمد" />
            </Field>
            <Field label="البريد الإلكتروني" icon={<EnvelopeIcon className="w-4 h-4" />} error={errors.email?.message}>
              <input {...register('email')} type="email" className="input" placeholder="user@health-ly.org" autoComplete="off" />
            </Field>
            <Field label="كلمة المرور المؤقتة" icon={<KeyIcon className="w-4 h-4" />} error={errors.password?.message}>
              <input {...register('password')} type="text" className="input" placeholder="8 أحرف على الأقل" autoComplete="new-password" />
              <p className="text-[11px] text-gray-500 mt-1">سيُجبر المستخدم على تغييرها عند أول دخول.</p>
            </Field>
            <Field label="رقم الهاتف (اختياري)" icon={<PhoneIcon className="w-4 h-4" />} error={errors.phone?.message}>
              <input {...register('phone')} className="input" placeholder="218..." />
            </Field>
            <Field label="الدور" error={errors.role?.message}>
              <select {...register('role')} className="input">
                {ROLES.map((r) => <option key={r} value={r}>{roleLabels[r]}</option>)}
              </select>
            </Field>
            <Field label="الجهة (اختيارية)" icon={<BuildingOffice2Icon className="w-4 h-4" />} error={errors.organizationId?.message}>
              <select {...register('organizationId')} className="input">
                <option value="">— لا شيء —</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.nameAr || o.name}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('mustChangePassword')} className="rounded" />
              <span>إجبار تغيير كلمة المرور عند أول دخول</span>
            </label>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[40px]">إلغاء</button>
              <button type="submit" disabled={isPending} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[40px]">
                {isPending ? 'جارٍ الإنشاء…' : 'إنشاء'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function EditUserModal({
  user, orgs, onClose, onSubmit, isPending,
}: {
  user: UserRow;
  orgs: Array<{ id: string; name?: string | null; nameAr?: string }>;
  onClose: () => void;
  onSubmit: (data: EditUserForm) => void;
  isPending: boolean;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<EditUserForm>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email: user.email,
      fullName: user.fullName,
      phone: user.phone || '',
      role: user.role,
      organizationId: user.organizationId || '',
    },
  });

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" dir="rtl">
          <div className="flex items-center justify-between p-4 border-b">
            <Dialog.Title className="text-lg font-semibold">تعديل المستخدم</Dialog.Title>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
            <Field label="الاسم الكامل" error={errors.fullName?.message}>
              <input {...register('fullName')} className="input" />
            </Field>
            <Field label="البريد الإلكتروني" error={errors.email?.message}>
              <input {...register('email')} type="email" className="input" />
            </Field>
            <Field label="رقم الهاتف" error={errors.phone?.message}>
              <input {...register('phone')} className="input" />
            </Field>
            <Field label="الدور" error={errors.role?.message}>
              <select {...register('role')} className="input">
                {ROLES.map((r) => <option key={r} value={r}>{roleLabels[r]}</option>)}
              </select>
            </Field>
            <Field label="الجهة" error={errors.organizationId?.message}>
              <select {...register('organizationId')} className="input">
                <option value="">— لا شيء —</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.nameAr || o.name}</option>)}
              </select>
            </Field>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[40px]">إلغاء</button>
              <button type="submit" disabled={isPending} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[40px]">
                {isPending ? 'جارٍ الحفظ…' : 'حفظ التعديلات'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function ResetPasswordModal({
  user, onClose, onSubmit, isPending,
}: {
  user: UserRow;
  onClose: () => void;
  onSubmit: (data: ResetPwForm) => void;
  isPending: boolean;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<ResetPwForm>({
    resolver: zodResolver(resetPwSchema),
    defaultValues: { mustChangePassword: true },
  });

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-md w-full" dir="rtl">
          <div className="flex items-center justify-between p-4 border-b">
            <Dialog.Title className="text-lg font-semibold">إعادة تعيين كلمة المرور</Dialog.Title>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
            <p className="text-sm text-gray-600">
              المستخدم: <strong>{user.fullName}</strong> ({user.email})
            </p>
            <Field label="كلمة المرور الجديدة" icon={<KeyIcon className="w-4 h-4" />} error={errors.newPassword?.message}>
              <input {...register('newPassword')} type="text" className="input" placeholder="8 أحرف على الأقل" autoComplete="new-password" />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('mustChangePassword')} className="rounded" />
              <span>إجبار المستخدم على تغييرها عند الدخول التالي</span>
            </label>
            <p className="text-xs text-amber-600 bg-amber-50 rounded p-2 border border-amber-200">
              ملاحظة: إعادة التعيين تُلغي أيضاً قفل الحساب الناتج عن محاولات الدخول الفاشلة.
            </p>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[40px]">إلغاء</button>
              <button type="submit" disabled={isPending} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 min-h-[40px]">
                {isPending ? 'جارٍ التعيين…' : 'إعادة التعيين'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function Field({
  label, icon, error, children,
}: {
  label: string;
  icon?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <span className="inline-flex items-center gap-1">{icon}{label}</span>
      </label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
